import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  Download,
  Trash2,
  Search,
  ShoppingBag,
  CheckCircle,
  RotateCcw,
  Package,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { OrderGroupSummary } from '@terra/api-client';
import type { settlements } from '@terra/contracts';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  toast,
} from '@terra/ui';
import { describePayment } from '@terra/utils';

import { ReturnQueue } from '../components/ReturnQueue';
import { api } from '../lib/api';

function toSentenceCase(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

// Order status → customer-facing delivery label.
const DELIVERY_LABEL: Record<string, string> = {
  PENDING_PAYMENT: 'Pending',
  PLACED: 'Processing',
  PARTIALLY_FULFILLED: 'Partially Fulfilled',
  COMPLETED: 'Delivered',
  CANCELLED: 'Cancelled',
};
const DELIVERY_TONE: Record<string, BadgeTone> = {
  PENDING_PAYMENT: 'warning',
  PLACED: 'neutral',
  PARTIALLY_FULFILLED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};
const PAID_STATES = new Set(['CAPTURED', 'PAID', 'COD_PAID']);

const NEXT_STATUS: Record<string, string> = {
  // Admin may accept on the vendor's behalf (e.g. in-house orders).
  PLACED: 'CONFIRMED',
  CONFIRMED: 'PACKED',
  PACKED: 'SHIPPED',
  SHIPPED: 'DELIVERED',
};

// ── settlement stage (where this order's money is) ──────────────────────────
//
// Sits beside Delivery Status because the two are different questions. An
// order can be "Delivered" and the seller still not have a rupee — it is in
// the return window, or the courier has the cash, or the run has not happened.
// This column answers the one the previous table left blank.

const SETTLEMENT_LABEL: Record<settlements.OrderSettlementStage, string> = {
  NOT_DELIVERED: 'Not yet delivered',
  AWAITING_COD_REMITTANCE: 'Awaiting COD cash',
  IN_RETURN_WINDOW: 'Return window',
  ELIGIBLE: 'Eligible to settle',
  SETTLED: 'Settled',
  PAID_OUT: 'Paid out',
  HELD: 'On hold',
  REVERSED: 'Reversed',
  CANCELLED: 'Cancelled',
};

const SETTLEMENT_TONE: Record<settlements.OrderSettlementStage, BadgeTone> = {
  NOT_DELIVERED: 'neutral',
  AWAITING_COD_REMITTANCE: 'warning',
  IN_RETURN_WINDOW: 'neutral',
  ELIGIBLE: 'gold',
  SETTLED: 'success',
  PAID_OUT: 'success',
  HELD: 'danger',
  REVERSED: 'danger',
  CANCELLED: 'neutral',
};

/** The stage furthest back in the pipeline wins, so a row never overstates progress. */
const STAGE_ORDER: settlements.OrderSettlementStage[] = [
  'HELD',
  'REVERSED',
  'AWAITING_COD_REMITTANCE',
  'NOT_DELIVERED',
  'IN_RETURN_WINDOW',
  'ELIGIBLE',
  'SETTLED',
  'PAID_OUT',
  'CANCELLED',
];

function summariseSettlement(
  o: OrderGroupSummary,
  byId: Map<string, settlements.OrderSettlementStatus>,
): settlements.OrderSettlementStatus | null {
  const stages = (o.subOrders ?? [])
    .map((sub) => byId.get(sub.id))
    .filter((x): x is settlements.OrderSettlementStatus => Boolean(x));
  if (stages.length === 0) return null;
  // Cancelled only counts if every sub-order is cancelled.
  const live = stages.filter((x) => x.stage !== 'CANCELLED');
  const pool = live.length > 0 ? live : stages;
  return pool.reduce((worst, cur) =>
    STAGE_ORDER.indexOf(cur.stage) < STAGE_ORDER.indexOf(worst.stage) ? cur : worst,
  );
}

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

// ── derived helpers ──────────────────────────────────────────────────────────

function productCount(o: OrderGroupSummary): number {
  return (o.subOrders ?? []).reduce((s, sub) => s + (sub._count?.lineItems ?? 0), 0);
}
function sellerLabel(o: OrderGroupSummary): string {
  const subs = o.subOrders ?? [];
  if (subs.length === 0) return '—';
  if (subs.every((s) => s.isInHouse)) return 'Inhouse Order';
  const first = subs[0]?.vendor?.storeName ?? 'Seller';
  return subs.length > 1 ? `${first} +${subs.length - 1}` : first;
}
function latestPayment(o: OrderGroupSummary) {
  return (o.payments ?? [])[0];
}
function isPaid(o: OrderGroupSummary): boolean {
  const p = latestPayment(o);
  return !!p && PAID_STATES.has(p.status);
}
function paymentMethodLabel(o: OrderGroupSummary): string {
  const p = latestPayment(o);
  if (!p) return '—';
  if (p.provider === 'RAZORPAY') return 'Razorpay';
  if (p.provider === 'COD') return 'Cash on Delivery';
  return p.provider;
}

// ── icons are now from lucide-react ──────────────────────────────────────────

// ── orders list ──────────────────────────────────────────────────────────────

interface OrderFilter {
  status?: string;
  fulfillmentType?: 'DELIVERY' | 'PICKUP';
  channel?: 'INHOUSE' | 'SELLER';
}

function OrdersListView({
  title,
  description,
  filter,
}: {
  title: string;
  description: string;
  filter: OrderFilter;
}) {
  const qc = useQueryClient();

  const [searchDraft, setSearchDraft] = useState('');
  const [deliveryDraft, setDeliveryDraft] = useState('');
  const [paymentDraft, setPaymentDraft] = useState('');
  const [applied, setApplied] = useState({ search: '', delivery: '', payment: '' });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-orders', filter, applied.search, applied.delivery],
    queryFn: () =>
      api.orders.listAll({
        pageSize: 100,
        ...(applied.search ? { q: applied.search } : {}),
        ...(applied.delivery
          ? { status: applied.delivery }
          : filter.status
            ? { status: filter.status }
            : {}),
        ...(filter.fulfillmentType ? { fulfillmentType: filter.fulfillmentType } : {}),
        ...(filter.channel ? { channel: filter.channel } : {}),
      }),
  });

  const rows = useMemo(() => {
    const all = data?.data ?? [];
    if (applied.payment === 'PAID') return all.filter(isPaid);
    if (applied.payment === 'UNPAID') return all.filter((o) => !isPaid(o));
    return all;
  }, [data, applied.payment]);

  // One request for the whole page of sub-orders, keyed by the ids on screen
  // so paging or filtering refetches only when the set actually changes.
  const subOrderIds = useMemo(
    () => rows.flatMap((o) => (o.subOrders ?? []).map((sub) => sub.id)),
    [rows],
  );
  const settlementStages = useQuery({
    queryKey: ['admin-order-settlement-stages', subOrderIds],
    queryFn: () => api.settlements.orderStatuses(subOrderIds),
    enabled: subOrderIds.length > 0,
    staleTime: 30_000,
  });
  const stageById = useMemo(
    () => new Map((settlementStages.data ?? []).map((x) => [x.subOrderId, x])),
    [settlementStages.data],
  );

  const cancelOne = useMutation({
    mutationFn: (id: string) => api.orders.adminCancelOrder(id, 'Cancelled by admin'),
    onSuccess: () => {
      toast.success('Order cancelled');
      void qc.invalidateQueries({ queryKey: ['admin-orders'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not cancel this order'),
  });
  // Only a cancelled order that never took money can go; the API says why
  // when it refuses (a payment, an invoice, a shipment…), so that is shown.
  const deleteOne = useMutation({
    mutationFn: (id: string) => api.orders.adminDeleteOrder(id),
    onSuccess: () => {
      toast.success('Order deleted');
      void qc.invalidateQueries({ queryKey: ['admin-orders'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not delete this order'),
  });
  const transitionSub = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.orders.adminTransitionSubOrder(id, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-orders'] }),
  });
  const bulkCancel = useMutation({
    mutationFn: async (ids: string[]) => {
      return api.orders.bulkCancelOrders(ids, 'Bulk cancelled by admin');
    },
    onSuccess: (data) => {
      setSelected(new Set());
      setBulkAction('');
      if (data?.failed > 0)
        toast.error(`${data.failed} order(s) could not be cancelled: ${data.errors[0] ?? ''}`);
      else toast.success(`${data.successful} order(s) cancelled`);
      void qc.invalidateQueries({ queryKey: ['admin-orders'] });
    },
  });

  function applyFilters() {
    setApplied({ search: searchDraft.trim(), delivery: deliveryDraft, payment: paymentDraft });
  }
  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((o) => o.id))));
  }
  function runBulk() {
    if (bulkAction === 'CANCEL' && selected.size > 0) {
      if (window.confirm(`Cancel ${selected.size} selected order(s)?`))
        bulkCancel.mutate([...selected]);
    }
  }
  function downloadOrder(o: OrderGroupSummary) {
    const lines = [
      ['Order Code', o.orderNumber],
      ['Customer', o.customer?.name ?? o.customer?.email ?? ''],
      ['Seller', sellerLabel(o)],
      ['Products', String(productCount(o))],
      ['Amount', formatPaise(o.grandTotalPaise)],
      ['Delivery Status', DELIVERY_LABEL[o.status] ?? o.status],
      ['Payment Method', paymentMethodLabel(o)],
      ['Payment Status', describePayment(o.payments, 'admin', { orderStatus: o.status }).label],
      ['Placed', new Date(o.createdAt).toLocaleString()],
    ];
    const csv = lines.map(([k, v]) => `"${k}","${String(v).replace(/"/g, '""')}"`).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-${o.orderNumber}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const allChecked = rows.length > 0 && selected.size === rows.length;

  return (
    <div className="pb-8">
      {/* Topbar renders the title, no local header needed here */}

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Toolbar */}
        <div className="border-border/60 flex flex-col flex-wrap items-center justify-between gap-4 border-b px-5 py-4 sm:flex-row">
          <div className="flex items-center gap-3">
            <div className="bg-ink text-surface grid h-9 w-9 place-items-center rounded-xl shadow-sm">
              <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-10 rounded-xl border px-3 text-[13px] font-medium outline-none transition-all focus:ring-2"
              >
                <option value="">Bulk Action</option>
                <option value="CANCEL">Cancel orders</option>
              </select>
              <button
                type="button"
                disabled={!bulkAction || selected.size === 0 || bulkCancel.isPending}
                onClick={runBulk}
                className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <select
              value={deliveryDraft}
              onChange={(e) => setDeliveryDraft(e.target.value)}
              className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-all focus:ring-2 sm:w-auto"
            >
              <option value="">All Deliveries</option>
              {Object.entries(DELIVERY_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>

            <select
              value={paymentDraft}
              onChange={(e) => setPaymentDraft(e.target.value)}
              className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-all focus:ring-2 sm:w-auto"
            >
              <option value="">All Payments</option>
              <option value="PAID">Paid</option>
              <option value="UNPAID">Un-Paid</option>
            </select>

            <div className="relative w-full sm:w-60">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
              </div>
              <input
                type="text"
                placeholder="Search code & Enter"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
                className="border-border/60 bg-surface-subtle text-ink focus:border-ink focus:ring-ink/20 focus:bg-surface placeholder:text-ink-subtle h-10 w-full rounded-xl border pl-9 pr-3 text-[13px] font-medium outline-none transition-all focus:ring-2"
              />
            </div>
            <button
              onClick={applyFilters}
              className="bg-surface-subtle text-ink hover:bg-surface-subtle/80 border-border/60 flex h-10 w-full items-center justify-center rounded-xl border px-5 text-[13px] font-bold shadow-sm transition-all sm:w-auto"
            >
              Filter
            </button>
          </div>
        </div>

        {/* Table */}
        <div>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Spinner size="md" />
              <span className="text-ink-muted text-[13px] font-medium">Fetching orders...</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12">
              <EmptyState title="No orders" description="No orders match the current filters." />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-12 py-3 pl-5 pr-3">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAll}
                        className="accent-ink h-4 w-4 cursor-pointer rounded border-gray-300"
                      />
                    </th>
                    <th className="py-3 pr-4">Order Code</th>
                    <th className="py-3 pr-4">Items</th>
                    <th className="py-3 pr-4">Customer</th>
                    <th className="py-3 pr-4">Seller</th>
                    <th className="py-3 pr-4">Amount</th>
                    <th className="py-3 pr-4">Delivery Status</th>
                    <th className="py-3 pr-4">Payment</th>
                    <th className="py-3 pr-4">Settlement</th>
                    <th className="py-3 pl-4 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((o, idx: number) => {
                    const paid = isPaid(o);
                    // COD shows where the cash is: due, with the courier, or banked.
                    const payView = describePayment(o.payments, 'admin', { orderStatus: o.status });
                    return (
                      <Fragment key={o.id}>
                        <motion.tr
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: (idx % 15) * 0.02 }}
                          className={`hover:bg-surface-subtle/30 group transition-colors ${expanded === o.id ? 'bg-surface-subtle/30' : ''}`}
                        >
                          <td className="w-12 py-3 pl-5 pr-3">
                            <input
                              type="checkbox"
                              checked={selected.has(o.id)}
                              onChange={() => toggleRow(o.id)}
                              className="accent-ink h-4 w-4 cursor-pointer rounded border-gray-300"
                            />
                          </td>
                          <td className="text-ink py-3 pr-4 font-mono text-[12px] font-bold">
                            #{o.orderNumber}
                          </td>
                          <td className="text-ink-muted py-3 pr-4 text-[13px] font-bold">
                            {productCount(o)}
                          </td>
                          <td className="text-ink py-3 pr-4 font-medium">
                            {o.customer?.name || 'Guest'}
                          </td>
                          <td className="text-ink-muted py-3 pr-4 font-medium">{sellerLabel(o)}</td>
                          <td className="text-ink py-3 pr-4 font-bold">
                            {formatPaise(o.grandTotalPaise)}
                          </td>
                          <td className="py-3 pr-4">
                            <Badge
                              tone={DELIVERY_TONE[o.status] ?? 'neutral'}
                              className="px-1.5 py-0.5 text-[10px] font-bold"
                            >
                              {DELIVERY_LABEL[o.status] ?? toSentenceCase(o.status)}
                            </Badge>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-ink-muted text-[11px] font-semibold">
                                {paymentMethodLabel(o)}
                              </span>
                              <span
                                className={`inline-flex rounded text-[10px] font-bold tracking-wide ${
                                  paid
                                    ? 'text-green-600'
                                    : payView.key === 'COD_COLLECTED'
                                      ? 'text-amber-600'
                                      : 'text-red-500'
                                }`}
                                title={payView.detail ?? undefined}
                              >
                                {payView.isCod || paid ? payView.label : 'Un-Paid'}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            {(() => {
                              const st = summariseSettlement(o, stageById);
                              if (!st) {
                                return (
                                  <span className="text-ink-subtle text-[11px] font-semibold">
                                    {settlementStages.isLoading ? '…' : '—'}
                                  </span>
                                );
                              }
                              return (
                                <div className="flex flex-col items-start gap-1">
                                  <Badge tone={SETTLEMENT_TONE[st.stage]}>
                                    {SETTLEMENT_LABEL[st.stage]}
                                  </Badge>
                                  {st.stage === 'IN_RETURN_WINDOW' && st.eligibleAt && (
                                    <span className="text-ink-subtle text-[10px] font-semibold">
                                      until {shortDate(st.eligibleAt)}
                                    </span>
                                  )}
                                  {st.stage === 'SETTLED' && st.netPayablePaise != null && (
                                    <span className="text-ink-subtle text-[10px] font-semibold tabular-nums">
                                      ₹
                                      {(st.netPayablePaise / 100).toLocaleString('en-IN', {
                                        minimumFractionDigits: 2,
                                      })}{' '}
                                      to seller
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="py-3 pl-4 pr-5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/orders/${o.id}`}
                                className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                                title="View Sub-orders & Details"
                              >
                                <Eye className="h-[18px] w-[18px]" strokeWidth={2} />
                              </Link>
                              <button
                                type="button"
                                onClick={() => downloadOrder(o)}
                                className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                                title="Download CSV"
                              >
                                <Download className="h-[18px] w-[18px]" strokeWidth={2} />
                              </button>
                              {o.status === 'CANCELLED' ? (
                                <button
                                  type="button"
                                  disabled={deleteOne.isPending}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Permanently delete order #${o.orderNumber}? This action cannot be undone.`,
                                      )
                                    )
                                      deleteOne.mutate(o.id);
                                  }}
                                  className="flex h-8 w-8 items-center justify-center rounded-lg text-red-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                  title="Delete order permanently"
                                >
                                  <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={cancelOne.isPending || o.status === 'COMPLETED'}
                                  onClick={() => {
                                    if (window.confirm(`Cancel order #${o.orderNumber}?`))
                                      cancelOne.mutate(o.id);
                                  }}
                                  className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                  title="Cancel order"
                                >
                                  <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                                </button>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selected.size > 0 && (
          <div className="border-border/60 bg-surface-subtle/30 border-t px-5 py-3">
            <p className="text-ink-muted text-[12px] font-semibold">
              {selected.size} order(s) selected.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export const AllOrdersPage = () => (
  <OrdersListView
    title="All Orders"
    description="Every order placed across the platform."
    filter={{}}
  />
);
export const InhouseOrdersPage = () => (
  <OrdersListView
    title="Inhouse Orders"
    description="Orders fulfilled from the platform's own first-party catalog."
    filter={{ channel: 'INHOUSE' }}
  />
);
export const SellerOrdersPage = () => (
  <OrdersListView
    title="Seller Orders"
    description="Orders fulfilled by marketplace sellers."
    filter={{ channel: 'SELLER' }}
  />
);
export const PickupOrdersPage = () => (
  <OrdersListView
    title="Pickup Point Orders"
    description="Orders the customer collects from a pickup point."
    filter={{ fulfillmentType: 'PICKUP' }}
  />
);
export const UnpaidOrdersPage = () => (
  <OrdersListView
    title="Unpaid Orders"
    description="Orders still awaiting payment."
    filter={{ status: 'PENDING_PAYMENT' }}
  />
);

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
  }),
};

const SUB_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING_PAYMENT: 'warning',
  PLACED: 'warning',
  CONFIRMED: 'success',
  PACKED: 'success',
  SHIPPED: 'success',
  DELIVERED: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'danger',
  RETURN_REQUESTED: 'warning',
  RETURN_APPROVED: 'warning',
  RETURN_RECEIVED: 'warning',
  REFUNDED: 'neutral',
};

export function ReturnsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('RETURN_REQUESTED');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-returns', statusFilter],
    queryFn: () => {
      const opts: Record<string, unknown> = { pageSize: 50 };
      if (statusFilter) opts.status = statusFilter;
      return api.orders.inHouseOrders(opts);
    },
  });

  const transition = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.orders.adminTransitionSubOrder(id, { status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-returns'] });
    },
  });

  const returnOrders = data?.data ?? [];

  return (
    <div className="space-y-5 pb-6">
      {/* Marketplace returns that need a decision: approval, seller disputes, inspections. */}
      <ReturnQueue />

      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="border-border/60 bg-surface flex flex-col gap-4 rounded-2xl border p-4 shadow-sm md:flex-row md:items-center md:justify-between"
      >
        <div className="flex flex-wrap gap-2">
          {['RETURN_REQUESTED', 'RETURN_APPROVED', 'RETURN_RECEIVED', 'REFUNDED', 'CLOSED'].map(
            (s) => {
              const isActive = statusFilter === s;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-lg px-3.5 py-1.5 text-[12px] font-semibold transition-all duration-300 ${
                    isActive
                      ? 'bg-ink ring-ink text-white shadow-sm ring-1'
                      : 'bg-surface-subtle text-ink-muted hover:bg-surface-subtle/80 hover:text-ink hover:ring-border/60 ring-1 ring-transparent'
                  }`}
                >
                  {toSentenceCase(s)}
                </button>
              );
            },
          )}
        </div>
      </motion.div>

      {isLoading ? (
        <div className="grid min-h-[300px] place-items-center">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-ink-muted text-[12px] font-medium">Loading returns…</p>
          </div>
        </div>
      ) : returnOrders.length === 0 ? (
        <div className="border-border/60 bg-surface-subtle/30 mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed py-16 text-center">
          <span className="bg-surface-subtle text-ink-muted mb-3 rounded-full p-3">
            <RotateCcw className="h-6 w-6" strokeWidth={1.5} />
          </span>
          <p className="text-ink text-[15px] font-bold">No returns found</p>
          <p className="text-ink-muted mt-1 text-[13px] font-medium">
            Return requests will appear here when they match this status.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {returnOrders.map((sub: any, i: number) => (
            <motion.div
              custom={i % 10}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              key={sub.id}
              className="border-border/60 bg-surface hover:ring-gold-300/50 group relative flex flex-col overflow-hidden rounded-xl border p-4 shadow-sm ring-1 ring-transparent transition-all duration-300 hover:-translate-y-0.5 hover:border-transparent hover:shadow-md"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-surface-subtle text-ink-muted group-hover:bg-gold-50 group-hover:text-gold-600 grid h-10 w-10 place-items-center rounded-lg transition-colors duration-300">
                    <RotateCcw className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-ink text-[14px] font-bold tracking-tight">
                      #{sub.orderGroup?.orderNumber ?? '—'}
                    </span>
                    <p className="text-ink-muted mt-0.5 text-[12px] font-medium">
                      {sub.orderGroup?.customer?.name ??
                        sub.orderGroup?.customer?.email ??
                        'Guest Customer'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="hidden flex-col items-end sm:flex">
                    <span className="text-ink text-[14px] font-extrabold">
                      {formatPaise(sub.totalPaise ?? 0)}
                    </span>
                    <span className="text-ink-muted mt-0.5 text-[11px] font-semibold uppercase tracking-wider">
                      Total
                    </span>
                  </div>
                  <div className="bg-border/60 h-8 w-px" />
                  <Badge
                    tone={SUB_STATUS_TONE[sub.status] ?? 'neutral'}
                    className="px-2 py-0.5 text-[11px] font-bold"
                  >
                    {toSentenceCase(sub.status)}
                  </Badge>
                </div>
              </div>

              {sub.lineItems && sub.lineItems.length > 0 && (
                <div className="border-border/40 bg-surface-subtle/30 mt-4 rounded-lg border p-3">
                  <span className="text-ink-subtle mb-2 block text-[11px] font-bold uppercase tracking-wider">
                    Items returned ({sub.lineItems.length})
                  </span>
                  <div className="grid gap-2">
                    {sub.lineItems.map((li: any) => (
                      <div
                        key={li.id}
                        className="border-border/40 bg-surface flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 shadow-sm"
                      >
                        <span className="text-ink text-[13px] font-semibold">
                          {li.productTitle}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="bg-gold-50 text-gold-700 rounded px-1.5 py-0.5 text-[11px] font-bold">
                            × {li.qty}
                          </span>
                          <span className="text-ink-muted text-[12px] font-bold">
                            {formatPaise(li.lineSubtotalPaise)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-border/60 mt-4 flex flex-wrap items-center gap-3 border-t pt-4">
                {sub.status === 'RETURN_REQUESTED' && (
                  <>
                    <button
                      className="bg-ink hover:bg-ink/90 flex h-8 items-center justify-center gap-1.5 rounded-lg px-4 text-[12px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50"
                      onClick={() => transition.mutate({ id: sub.id, status: 'RETURN_APPROVED' })}
                      disabled={transition.isPending}
                    >
                      <CheckCircle className="h-3.5 w-3.5" /> Approve return
                    </button>
                    <button
                      className="border-border/60 bg-surface-subtle/50 text-ink-muted flex h-8 items-center justify-center gap-1.5 rounded-lg border px-4 text-[12px] font-bold transition-all hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                      onClick={() => transition.mutate({ id: sub.id, status: 'CLOSED' })}
                      disabled={transition.isPending}
                    >
                      Reject return
                    </button>
                  </>
                )}
                {sub.status === 'RETURN_APPROVED' && (
                  <button
                    className="bg-ink hover:bg-ink/90 flex h-8 items-center justify-center gap-1.5 rounded-lg px-4 text-[12px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50"
                    onClick={() => transition.mutate({ id: sub.id, status: 'RETURN_RECEIVED' })}
                    disabled={transition.isPending}
                  >
                    <Package className="h-3.5 w-3.5" /> Mark items received
                  </button>
                )}
                {sub.status === 'RETURN_RECEIVED' && (
                  <button
                    className="bg-gold-500 text-zone-900 hover:bg-gold-400 hover:shadow-glow-gold flex h-8 items-center justify-center gap-1.5 rounded-lg px-4 text-[12px] font-bold shadow-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    onClick={() => transition.mutate({ id: sub.id, status: 'REFUNDED' })}
                    disabled={transition.isPending}
                  >
                    Confirm refund & complete
                  </button>
                )}
                {['REFUNDED', 'CLOSED'].includes(sub.status) && (
                  <div className="bg-surface-subtle/50 text-ink-muted flex w-full items-center justify-center rounded-lg px-4 py-2 text-center text-[12px] font-medium italic">
                    No further actions required for this return
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
